import { number, usdValue } from './positionRollup';

export const completeSum = values => values.length && values.every(v => number(v) !== null)
  ? values.reduce((sum, v) => sum + Number(v), 0) : null;

// Whether NAV subtracts any liabilities, to the cent. Brian, 21 October 2026:
// *"please remove the liabilities box now"*, after the share obligations that
// were its only content were released (D-275). The box is gone; the term
// stays in each NAV formula only while it is not nil, so the figures on a page
// still add up to the NAV beside them if a liability is ever booked again.
export function hasLiabilities(summary) {
  return Math.abs(Number(summary?.total_liabilities || 0)) >= 0.005;
}

export function postedResult(reports) {
  return completeSum(reports.map(r => r?.net_income?.mtd?.usd));
}

export function portfolioResult(reports) {
  return completeSum(reports.map(r => r?.portfolio_mtd?.usd));
}

export function portfolioResultBreakdown(reports, view = 'deals') {
  const available = reports.length > 0 && reports.every(r => Array.isArray(r.portfolio_mtd?.deals));
  const grouped = new Map();
  if (available) for (const report of reports) for (const deal of report.portfolio_mtd.deals) {
    for (const line of deal.lines) {
      const valuation = line.account === 'valuation_movement';
      const realized = ['Income:Realized Gains', 'Expense:RealizedLoss'].includes(line.account);
      const category = valuation ? 'Unrealized movement' : realized ? 'Realized gains / losses' : line.account?.startsWith('Expense:') ? 'Charges' : 'Posted income / gains';
      const key = view === 'deals' ? deal.deal_id ?? 'unallocated' : category;
      const row = grouped.get(key) || {key, name: view === 'deals' ? deal.name : category, amounts: [], lines: []};
      row.amounts.push(line.usd);
      row.lines.push({...line, name: view === 'deals' ? line.name : `${deal.name} · ${line.name}`, book: report.bookName, amount: number(line.usd)});
      grouped.set(key, row);
    }
  }
  const rows = [...grouped.values()].map(row => ({...row, amount: completeSum(row.amounts)}))
    .sort((a,b) => Math.abs(b.amount || 0) - Math.abs(a.amount || 0));
  return {available, rows, result: portfolioResult(reports), posted: postedResult(reports), residual: null};
}

export function resultBreakdown(reports) {
  const groups = new Map();
  for (const report of reports) {
    for (const [section, sign] of [['income', 1], ['expenses', -1]]) {
      for (const group of report?.[section]?.groups || []) {
        const key = `${section}:${group.group}`;
        const item = groups.get(key) || {key, name: group.group, section, amounts: [], lines: []};
        item.amounts.push(number(group.total?.mtd?.usd) === null ? null : sign * Number(group.total.mtd.usd));
        for (const line of group.lines || []) {
          const amount = number(line.mtd?.usd);
          if (amount === 0) continue;
          item.lines.push({name: line.label || line.account, book: report.bookName, amount: amount === null ? null : sign * amount});
        }
        groups.set(key,item);
      }
    }
  }
  const rows = [...groups.values()].map(g => ({...g, amount: completeSum(g.amounts)})).filter(g => g.amount !== 0 || g.lines.length);
  const result = postedResult(reports), sum = rows.length ? completeSum(rows.map(r => r.amount)) : (reports.length ? 0 : null);
  return {rows, result, residual: result === null || sum === null ? null : Math.round((result - sum)*100)/100};
}

export function dealResultBreakdown(reports) {
  const available = reports.length > 0 && reports.every(r => Array.isArray(r.mtd_deals));
  if (!available) return {rows: [], result: postedResult(reports), residual: null, available: false};
  const grouped = new Map();
  for (const report of reports) for (const deal of report.mtd_deals) {
    const key = deal.deal_id ?? 'unallocated';
    const row = grouped.get(key) || {key, name: deal.name, amounts: [], lines: [], unallocated: deal.deal_id == null};
    row.amounts.push(deal.usd);
    row.lines.push(...(deal.lines || []).map(line => ({name: line.name, book: report.bookName, amount: number(line.usd)})));
    grouped.set(key,row);
  }
  const rows = [...grouped.values()].map(row => ({...row, amount: completeSum(row.amounts)}))
    .sort((a,b) => Number(a.unallocated)-Number(b.unallocated) || Math.abs(b.amount || 0)-Math.abs(a.amount || 0));
  const result = postedResult(reports), sum = rows.length ? completeSum(rows.map(r => r.amount)) : 0;
  return {rows, result, available, residual: result === null || sum === null ? null : Math.round((result-sum)*100)/100};
}

export function portfolioExposures(data) {
  const positions = data?.positions || [];
  const instruments = new Map(), currencies = new Map();
  let complete = Boolean(data?.positions);
  for (const p of positions) {
    const value = usdValue(p);
    if (value === null) { complete = false; continue; }
    const key = p.security_id ?? `${p.ticker}:${p.name}:${p.currency}`;
    const row = instruments.get(key) || { name: p.name || p.ticker || 'Unnamed instrument', value: 0 };
    row.value += value;
    instruments.set(key, row);
    currencies.set(p.currency || 'Unknown', (currencies.get(p.currency || 'Unknown') || 0) + value);
  }
  for (const p of data?.cash || []) {
    const value = number(p.balance_usd);
    if (value === null) { complete = false; continue; }
    currencies.set(p.currency || 'Unknown', (currencies.get(p.currency || 'Unknown') || 0) + value);
  }
  return { complete, instruments: [...instruments.values()].sort((a,b) => Math.abs(b.value)-Math.abs(a.value)),
    currencies: [...currencies].map(([name,value]) => ({name,value})).sort((a,b) => Math.abs(b.value)-Math.abs(a.value)) };
}

export function maturityEvents(data) {
  if (!data?.as_of_date) return [];
  const end = new Date(`${data.as_of_date}T12:00:00Z`);
  end.setUTCDate(end.getUTCDate() + 90);
  const until = end.toISOString().slice(0,10), groups = new Map();
  for (const p of data.positions || []) {
    if (!p.maturity_date || p.maturity_date < data.as_of_date || p.maturity_date > until || number(p.face_value) === null) continue;
    const key = `${p.security_id ?? p.name}:${p.currency}:${p.maturity_date}`;
    const row = groups.get(key) || { name: p.name || p.ticker, date: p.maturity_date, currency: p.currency, amount: 0, deal_id: p.deal_id ?? null };
    row.amount += Number(p.face_value);
    groups.set(key,row);
  }
  return [...groups.values()].filter(e => e.amount > 0).sort((a,b) => a.date.localeCompare(b.date));
}
